import React from 'react';
import { Card, EmptyState, MaterialIcon } from '../../ui';
import { getPeopleRoster, getSessions, summarizeFunnel } from '../../../services/activityService';
import { queryOverrides } from '../../../rag/queryOverrides';
import FunnelStrip from './FunnelStrip';
import TopDocumentsCard from './TopDocumentsCard';
import {
  formatDate, formatDuration, formatExact, formatRelative, formatRole, formatSection, formatShort, plural,
} from './activityFormat';

const HOUR_MS = 60 * 60 * 1000;

// Bar widths in twelfths, as literal Tailwind classes (no inline styles).
const BAR_WIDTHS = [
  'w-1/12', 'w-2/12', 'w-3/12', 'w-4/12', 'w-5/12', 'w-6/12',
  'w-7/12', 'w-8/12', 'w-9/12', 'w-10/12', 'w-11/12', 'w-full',
];
const barWidthClass = (value, max) => BAR_WIDTHS[Math.min(11, Math.max(0, Math.round((value / max) * 12) - 1))];

const USAGE = {
  none: { label: 'Not yet', className: 'bg-section-bg text-slate-500' },
  dormant: { label: 'Dormant', className: 'bg-section-bg text-slate-600' },
  light: { label: 'Light', className: 'bg-amber-50 text-amber-800' },
  medium: { label: 'Medium', className: 'bg-secondary/15 text-secondary' },
  heavy: { label: 'Heavy', className: 'bg-primary-dark text-white' },
};

const ONBOARDING = {
  onboarded: { label: 'Onboarded', icon: 'check_circle' },
  signed_in_no_visit: { label: 'Signed in, no visit', icon: 'hourglass_top' },
  invited: { label: 'Invited', icon: 'mail' },
};

const SEGMENTS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'never', label: 'Never visited', test: (r) => !r.first_login_at },
  { key: 'active', label: 'Active 30 d', test: (r) => r.active_days_30d > 0 },
  { key: 'dormant', label: 'Dormant', test: (r) => r.usage_tier === 'dormant' },
];

const UsageBadge = ({ tier }) => {
  const u = USAGE[tier] || USAGE.none;
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${u.className}`}>
      {u.label}
    </span>
  );
};

const StatusLabel = ({ row }) => {
  const s = ONBOARDING[row.onboarding_status] || ONBOARDING.invited;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-dark">
      <MaterialIcon name={s.icon} size={14} className={row.onboarding_status === 'onboarded' ? 'text-secondary' : 'text-slate-400'} />
      {s.label}
    </span>
  );
};

const LastVisit = ({ value }) => (value ? (
  <span className="flex flex-col">
    <span className="text-xs font-semibold text-primary-dark">{formatExact(value)}</span>
    <span className="text-[11px] text-slate-500">{formatRelative(value)}</span>
  </span>
) : <span className="text-xs text-slate-400">Never</span>);

/** Auth sign-in note, shown only when it differs from the last real visit by more than an hour. */
const signInNote = (row) => {
  if (!row.auth_last_sign_in_at) return null;
  const auth = new Date(row.auth_last_sign_in_at).getTime();
  const visit = row.last_login_at ? new Date(row.last_login_at).getTime() : null;
  if (visit !== null && Math.abs(auth - visit) <= HOUR_MS) return null;
  return `Account sign-in ${formatDate(row.auth_last_sign_in_at)}${visit === null ? ' (portal never opened)' : ''}`;
};

const sortRows = (rows) => [...rows].sort((a, b) => {
  const aNever = !a.first_login_at;
  const bNever = !b.first_login_at;
  if (aNever !== bNever) return aNever ? -1 : 1;
  if (aNever) return new Date(b.invited_at || 0) - new Date(a.invited_at || 0);
  return new Date(b.last_login_at || b.last_seen_at || 0) - new Date(a.last_login_at || a.last_seen_at || 0);
});

const PersonDetail = ({ row, hoaId, includeAdmin, questionCount = 0, onOpenQuestions }) => {
  const [sessions, setSessions] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!row.user_id) return undefined;
    let active = true;
    (async () => {
      try {
        const page = await getSessions({
          hoaId,
          userId: row.user_id,
          limit: 10,
          includeAdmin: includeAdmin || row.is_excluded,
          includeArchived: true,
        });
        if (active) setSessions(page.sessions);
      } catch (err) {
        if (active) setError(err?.message || 'Sessions could not be loaded.');
      }
    })();
    return () => { active = false; };
  }, [row.user_id, row.is_excluded, hoaId, includeAdmin]);

  const note = signInNote(row);
  const sections = Array.isArray(row.top_sections) ? row.top_sections : [];
  const maxSection = Math.max(1, ...sections.map((s) => s.views || 0));
  const docs = Array.isArray(row.top_documents) ? row.top_documents : [];
  const perSession = row.sessions_30d ? (row.events_30d / row.sessions_30d).toFixed(1) : '—';

  if (!row.user_id) {
    return (
      <p className="text-sm text-slate-600">
        Invited {formatExact(row.invited_at)}. No account has been created from this invitation yet.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {note && (
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white rounded-full px-3 py-1 border border-amber-100/60">
          <MaterialIcon name="key" size={14} className="text-secondary" /> {note}
        </p>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-4 border border-amber-100/40">
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Last 30 days</h4>
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-slate-500">Sessions</dt><dd className="font-bold text-primary-dark">{row.sessions_30d}</dd>
            <dt className="text-slate-500">Active days</dt><dd className="font-bold text-primary-dark">{row.active_days_30d}</dd>
            <dt className="text-slate-500">Events / session</dt><dd className="font-bold text-primary-dark">{perSession}</dd>
            <dt className="text-slate-500">All-time sessions</dt><dd className="font-bold text-primary-dark">{row.sessions_total}</dd>
            <dt className="text-slate-500">AI questions</dt>
            <dd className="font-bold text-primary-dark">
              {questionCount}
              {questionCount > 0 && onOpenQuestions && (
                <button
                  type="button"
                  onClick={() => onOpenQuestions({ id: row.user_id, name: row.full_name })}
                  className="ml-2 inline-flex items-center gap-0.5 text-xs font-bold text-secondary hover:underline"
                >
                  View <MaterialIcon name="arrow_forward" size={12} />
                </button>
              )}
            </dd>
          </dl>
        </div>

        <div className="bg-white rounded-2xl p-4 border border-amber-100/40">
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Documents opened</h4>
          {docs.length === 0 ? (
            <p className="text-sm text-slate-500">No documents opened yet.</p>
          ) : (
            <ul className="space-y-2">
              {docs.map((d) => (
                <li key={d.document_id || d.title} className="text-sm">
                  <span className={`font-semibold break-words ${d.still_exists === false ? 'text-slate-400' : 'text-primary-dark'}`}>
                    {d.title}
                  </span>
                  <span className="block text-[11px] text-slate-500">
                    {plural(d.views, 'view')} · last {formatShort(d.last_viewed_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl p-4 border border-amber-100/40">
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Sections browsed</h4>
          {sections.length === 0 ? (
            <p className="text-sm text-slate-500">No sections browsed yet.</p>
          ) : (
            <ul className="space-y-2">
              {sections.map((s) => (
                <li key={s.section} className="text-xs">
                  <div className="flex justify-between font-semibold text-primary-dark">
                    <span>{formatSection(s.section)}</span><span>{s.views}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-section-bg mt-1 overflow-hidden">
                    <div className={`h-full rounded-full bg-secondary ${barWidthClass(s.views, maxSection)}`} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 border border-amber-100/40">
        <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Last 10 sessions</h4>
        {error && <p className="text-sm text-red-700">{error}</p>}
        {!sessions && !error && <p className="text-sm text-slate-500">Loading…</p>}
        {sessions && sessions.length === 0 && <p className="text-sm text-slate-500">No sessions yet.</p>}
        {sessions && sessions.length > 0 && (
          <ul className="divide-y divide-amber-100/40">
            {sessions.map((s) => (
              <li key={s.session_key} className="py-2 text-xs flex flex-wrap gap-x-3 gap-y-1 items-center">
                <span className="font-semibold text-primary-dark">{formatShort(s.started_at)}</span>
                <span className="text-slate-500">{formatDuration(s.started_at, s.ended_at)}</span>
                <span className="text-slate-500">{plural(s.event_count, 'event')}</span>
                {s.doc_views > 0 && <span className="text-secondary font-semibold">{plural(s.doc_views, 'document')} opened</span>}
                {s.admin_view_as && <span className="text-secondary font-semibold">admin view-as</span>}
                <span className="text-slate-500">{(s.sections || []).map(formatSection).join(', ')}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/**
 * People view: onboarding funnel, most-opened documents and one row per person
 * (plus pending invitees), from admin_activity_roster.
 */
const PeopleRoster = ({ hoaId, includeAdmin, includeArchived, refreshKey, onOpenQuestions }) => {
  const [rows, setRows] = React.useState([]);
  const [questionCounts, setQuestionCounts] = React.useState(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [segment, setSegment] = React.useState('all');
  const [expanded, setExpanded] = React.useState(null);

  React.useEffect(() => {
    if (!hoaId) return undefined;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        // Fetch everyone once; the toggles filter client-side so the funnel
        // caption can say how many accounts are hidden.
        const [data, counts] = await Promise.all([
          getPeopleRoster({ hoaId, includeArchived: true, includeExcluded: true }),
          queryOverrides.getQuestionCountsByUser(hoaId),
        ]);
        if (!active) return;
        setRows(data);
        setQuestionCounts(counts);
        setError('');
      } catch (err) {
        if (!active) return;
        setRows([]);
        setError(err?.message || 'People could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [hoaId, refreshKey]);

  const funnel = React.useMemo(() => summarizeFunnel(rows), [rows]);
  const visibleRows = React.useMemo(() => sortRows(rows.filter((r) => (
    (includeAdmin || !r.is_excluded) && (includeArchived || r.account_state !== 'archived')
  ))), [rows, includeAdmin, includeArchived]);
  const segmentRows = visibleRows.filter((SEGMENTS.find((s) => s.key === segment) || SEGMENTS[0]).test);

  const toggle = (key) => setExpanded((current) => (current === key ? null : key));

  return (
    <div className="space-y-6">
      {!loading && !error && <FunnelStrip funnel={funnel} includeAdmin={includeAdmin} />}

      <TopDocumentsCard hoaId={hoaId} includeAdmin={includeAdmin} refreshKey={refreshKey} />

      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-bold text-primary-dark">People</h3>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter people">
            {SEGMENTS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSegment(s.key)}
                aria-pressed={segment === s.key}
                className={`px-3.5 py-1.5 text-xs font-bold rounded-full border transition-colors ${
                  segment === s.key
                    ? 'bg-secondary/10 border-secondary/40 text-secondary'
                    : 'bg-white border-amber-100/60 text-slate-600 hover:border-secondary/40'
                }`}
              >
                {s.label} <span className="opacity-70">{visibleRows.filter(s.test).length}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="py-10 text-center text-sm text-slate-500">Loading people…</p>
        ) : error ? (
          <p className="py-10 text-center text-sm text-red-700">{error}</p>
        ) : segmentRows.length === 0 ? (
          <EmptyState icon="group" title="Nobody in this group" body="Try another filter or toggle." />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto rounded-2xl border border-amber-100/40">
              <table className="w-full text-left">
                <thead className="bg-section-bg text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Person</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3">Invited</th>
                    <th className="px-3 py-3">First visit</th>
                    <th className="px-3 py-3">Last visit</th>
                    <th className="px-3 py-3">Sessions 30d / all</th>
                    <th className="px-3 py-3">Docs 30d / all</th>
                    <th className="px-3 py-3">Usage</th>
                    <th className="px-3 py-3">Last section</th>
                    <th className="px-3 py-3"><span className="sr-only">Expand</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100/40">
                  {segmentRows.map((r) => {
                    const open = expanded === r.person_key;
                    return (
                      <React.Fragment key={r.person_key}>
                        <tr onClick={() => toggle(r.person_key)} className="cursor-pointer hover:bg-main-bg/60 transition-colors align-top">
                          <td className="px-4 py-3">
                            <div className="text-sm font-bold text-primary-dark">{r.full_name}</div>
                            <div className="text-[11px] text-slate-500">
                              {[formatRole(r.role_name), r.account_state === 'archived' ? 'Archived' : null,
                                r.account_state === 'no_account' ? 'No account yet' : null,
                                r.is_excluded && r.role_name !== 'admin' ? 'Test account' : null]
                                .filter(Boolean).join(' · ')}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <StatusLabel row={r} />
                            {!r.invited_at && r.user_id && <div className="text-[11px] text-slate-500">Direct account</div>}
                          </td>
                          <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{r.invited_at ? formatDate(r.invited_at) : '—'}</td>
                          <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{r.first_login_at ? formatDate(r.first_login_at) : '—'}</td>
                          <td className="px-3 py-3 min-w-[11rem]"><LastVisit value={r.last_login_at} /></td>
                          <td className="px-3 py-3 text-sm font-semibold text-primary-dark">{r.sessions_30d} / {r.sessions_total}</td>
                          <td className="px-3 py-3 text-sm font-semibold text-primary-dark">{r.documents_opened_30d} / {r.documents_opened_total}</td>
                          <td className="px-3 py-3"><UsageBadge tier={r.usage_tier} /></td>
                          <td className="px-3 py-3 text-xs text-slate-600">{formatSection(r.last_section) || '—'}</td>
                          <td className="px-3 py-3">
                            <MaterialIcon name={open ? 'expand_less' : 'expand_more'} size={20} className="text-secondary" />
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={10} className="bg-section-bg px-4 py-4">
                              <PersonDetail
                                row={r}
                                hoaId={hoaId}
                                includeAdmin={includeAdmin}
                                questionCount={questionCounts.get(r.user_id) || 0}
                                onOpenQuestions={onOpenQuestions}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <ul className="md:hidden space-y-3">
              {segmentRows.map((r) => {
                const open = expanded === r.person_key;
                return (
                  <li key={r.person_key} className="bg-white rounded-2xl border border-amber-100/40">
                    <button type="button" onClick={() => toggle(r.person_key)} aria-expanded={open} className="w-full text-left p-4 flex gap-3">
                      <span className="min-w-0 flex-1 space-y-1.5">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-bold text-primary-dark break-words">{r.full_name}</span>
                          <UsageBadge tier={r.usage_tier} />
                        </span>
                        <span className="block text-[11px] text-slate-500">
                          {[formatRole(r.role_name), r.account_state === 'archived' ? 'Archived' : null,
                            r.account_state === 'no_account' ? 'No account yet' : null].filter(Boolean).join(' · ')}
                        </span>
                        <StatusLabel row={r} />
                        <span className="block text-xs text-slate-600">
                          Last visit: {r.last_login_at ? `${formatExact(r.last_login_at)} (${formatRelative(r.last_login_at)})` : 'never'}
                        </span>
                        <span className="block text-xs text-slate-600">
                          Sessions {r.sessions_30d} / {r.sessions_total} · Docs {r.documents_opened_30d} / {r.documents_opened_total}
                        </span>
                      </span>
                      <MaterialIcon name={open ? 'expand_less' : 'expand_more'} size={20} className="text-secondary shrink-0" />
                    </button>
                    {open && (
                      <div className="px-3 pb-3">
                        <div className="bg-section-bg rounded-2xl p-3">
                          <PersonDetail
                                row={r}
                                hoaId={hoaId}
                                includeAdmin={includeAdmin}
                                questionCount={questionCounts.get(r.user_id) || 0}
                                onOpenQuestions={onOpenQuestions}
                              />
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
};

export default PeopleRoster;
